import axios from "axios";

const getBaseUrl = () => {
  return process.env.REACT_APP_API_URL || "http://localhost:5000";
};

const api = axios.create({
  baseURL: `${getBaseUrl()}/api`,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: false,
  timeout: 60000,
});

let getAuthToken = () => localStorage.getItem("token");
let handleUnauthorized;

export const setApiAuthHandlers = ({ getToken, onUnauthorized }) => {
  if (typeof getToken === "function") {
    getAuthToken = getToken;
  }

  handleUnauthorized = onUnauthorized;
};

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken?.();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const method = config.method ? config.method.toUpperCase() : "GET";
      console.log(`[API] ${method} ${config.baseURL}${config.url}`);
    } catch {
      console.log("[API] Request Started");
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const hadAuthHeader = Boolean(error.config?.headers?.Authorization);

    if (error.response?.status === 401 && hadAuthHeader) {
      handleUnauthorized?.(error);
    }

    return Promise.reject(error);
  },
);

export const getImageUrl = (path, fallback = "image/default-avatar.png") => {
  if (path?.startsWith("http")) return path;

  const normalizeAssetPath = (assetPath) => {
    if (!assetPath) return null;
    return assetPath.startsWith("/") ? assetPath : `/${assetPath}`;
  };

  const frontendPublicPrefixes = [
    "image/",
    "party-logos/",
    "state-logos/",
    "State_Banner/",
    "assets/",
    "logo",
    "favicon",
  ];
  const baseUrl = getBaseUrl();

  if (!path) {
    return normalizeAssetPath(fallback);
  }

  if (path.startsWith("/")) {
    return path;
  }

  if (!path.includes("/")) {
    path = path.toLowerCase().includes("logo")
      ? `party-logos/${path}`
      : `image/${path}`;
  }

  if (frontendPublicPrefixes.some((prefix) => path.startsWith(prefix))) {
    return normalizeAssetPath(path);
  }

  return `${baseUrl}/${path}`;
};

export const states = {
  getAll: () => api.get("/states"),
  getByName: (name) => api.get(`/state/${name}`),
  lookupRepresentative: (params) =>
    api.get("/states/lookup-representative", { params }),
};

export const candidates = {
  getAll: () => api.get("/candidates"),
  getByName: (name) => api.get(`/candidates/${encodeURIComponent(name)}`),
  submitClaim: (formData) =>
    api.post("/candidates/claim", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  getMyClaims: () => api.get("/candidates/claims/me"),
  getDashboard: () => api.get("/candidate/dashboard"),
  postIssueUpdate: (payload) => api.post("/candidates/issues/update", payload),
};

export const issues = {
  getAssignedToMe: () => api.get("/issues/assigned-to-me"),
  respond: (id, payload) => api.post(`/issues/${id}/respond`, payload),
  resolve: (id, payload = {}) => api.patch(`/issues/${id}/resolve`, payload),
  getById: (id) => api.get(`/issues/${id}`),
};

export const admin = {
  getClaims: (status = "all") => api.get("/admin/claims", { params: { status } }),
  approveClaim: (id) => api.patch(`/admin/claims/${id}/approve`),
  rejectClaim: (id, payload) => api.patch(`/admin/claims/${id}/reject`, payload),
};

export default api;
